import { ChangeDetectorRef, Injectable } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import { FormControl, ValuesOf } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  delay,
  first,
  map,
  Observable,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { OrcamentoByIdResponse } from '../../../../models/orcamento.models';
import { NewApiOrcamentoService } from '../../../api/new-api-orcamento/new-api-orcamento.service';
import { FormOrchestratorTableData, FormTableColumns, TableFormService, UiFormTableComponent } from '@brunsker/libs/core/ui/table/src';
import { EditarOrcamentoDataDisplayColumns, MapperService, SelectClientService } from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import { NewApiProductService } from '../../../api/new-api-product/new-api-product.service';
import { UtilsBsHotToastService } from '@brunsker/libs/core/utils/hot-toast/src';
import { NewApiUserService } from '../../../api/new-api-user/new-api-user.service';
import { CartObservationsGroupComponent } from '@brunsker/libs/portal-do-vendedor/ui/observations-text-area';

export interface ItemsPedidoVendaFutura {
  obS_PROD?: string;
  qT_DISPONIVEL: number;
  vlprodutotabela: number;
  retiradaImediata: boolean;
  vlproduto: number;
  iD_FILIAL: number;
  codigo: number;
  desconto: number;
  totaL_ITEM: number;
  item: number;
  nomE_PRODUTO: string;
  nomE_MARCA: string;
  qtproduto: number;
  numseqped: number;
  multiplo: string;
  ambiente?: string;
  remove: boolean;
  subTotal: number;
}
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class VendaFuturaDomainService {
  hasClient = this.selectClientService.hasClient$$;
  columnsOptions: FormTableColumns[] =
    this.mapperService.createEditarOrcamentoColumnsOptions();
  orcamentoTv7$!: Observable<OrcamentoByIdResponse>;
  cartLength!: number;
  ambientes!: string[];
  dataDisplay$!: Observable<
    FormOrchestratorTableData<EditarOrcamentoDataDisplayColumns>[]
  >;
  table!: UiFormTableComponent<any>;
  removeControlsIndex: string[] = [];
  totalDiscount$$ = new BehaviorSubject<number>(0);
  orcamentoTotalCart$$ = new BehaviorSubject(0);
  freteControl = new FormControl<number>(0);
  keyTable = 'venda-futura';
  allProducts$$ = new BehaviorSubject<ItemsPedidoVendaFutura[]>([]);
  hasData!: boolean;
  clientId = 0;
  constructor(
    private readonly selectClientService: SelectClientService,
    private readonly newApiOrcamento: NewApiOrcamentoService,
    private readonly newApiProductService: NewApiProductService,
    private readonly tableFormService: TableFormService<ItemsPedidoVendaFutura>,
    private readonly toast: UtilsBsHotToastService,
    private readonly mapperService: MapperService,
    private readonly orcamentoApiService: NewApiOrcamentoService,
    private readonly newApiUserService: NewApiUserService,
    private readonly router: Router
  ) {}

  start(activatedRoute: ActivatedRoute, changeDetectorRef: ChangeDetectorRef) {
    this.tableFormService.keyTable = this.keyTable;
    activatedRoute.params
      .pipe(
        switchMap((params) =>
          this.newApiOrcamento.getOrcamentoById(params['id'])
        ),
        map((data) => {
          window.history.replaceState({}, '', `/venda-futura`);
          this.clientId = data.iD_CLIENTE;
          const mapItemsPedidoVendaFutura = data.items_pedido.map((item) => {
            return {
              obS_PROD: item.obS_PROD ?? '',
              qT_DISPONIVEL: item.qT_DISPONIVEL,
              vlproduto: item.vlproduto,
              vlprodutotabela: item.vlprodutotabela,
              retiradaImediata: item.retiradaImediata,
              iD_FILIAL: item.iD_FILIAL,
              codigo: item.codigo,
              desconto: item.desconto,
              totaL_ITEM: item.totaL_ITEM,
              item: item.item,
              nomE_PRODUTO: item.nomE_PRODUTO,
              nomE_MARCA: item.nomE_MARCA,
              qtproduto: item.qtproduto,
              numseqped: item.numseqped,
              multiplo: item.multiplo,
              ambiente: item.ambiente ?? '',
              remove: false,
              subTotal: item.vlproduto * item.qtproduto,
            };
          });
          this.allProducts$$.next(mapItemsPedidoVendaFutura);
        })
      )
      .subscribe();

    this.dataDisplay$ = this.mappedDisplayData(changeDetectorRef);

    this.tableFormService.formGroups[this.keyTable].valueChanges
      .pipe(
        startWith(this.tableFormService.formGroups[this.keyTable].value),
        untilDestroyed(this),
        this.mappedTableFormData()
      )
      .subscribe();
    changeDetectorRef.detectChanges();

    this.router.events
      .pipe(untilDestroyed(this), first())
      .subscribe((event: any) => {
        const firstBar = event.url.split('/')[1];
        if (firstBar == 'carrinho') {
          this.cancel();
          return;
        }
        this.router.navigate(['/']);
      });
  }

  createTableData(products: ItemsPedidoVendaFutura[]) {
    const currentClient = this.selectClientService.getCliente();
    const userStringify = sessionStorage.getItem('user');
    const user = userStringify && JSON.parse(userStringify);
    const MAP_DATA_COLUMNS = products.map(
      (cart: ItemsPedidoVendaFutura, index: number) => {
        const desconto$ = this.newApiProductService.getProductDiscount({
          CodigoFilial: cart.iD_FILIAL,
          CodigoCliente: currentClient?.id ?? this.clientId,
          CodigoVendedor: currentClient?.vendedor ?? undefined,
          CodigoParcela: currentClient?.planoDePagamento ?? undefined,
          CodigoProduto: cart.codigo ?? undefined,
          matricula: user?.idFuncionario,
        });

        return {
          produtos: `${cart.codigo} - ${cart.nomE_PRODUTO}`,
          filial: cart.iD_FILIAL,
          qT_DISPONIVEL: cart.qT_DISPONIVEL,
          desconto: cart.desconto,
          valorUnitario: cart.vlprodutotabela,
          valorUnitarioComDesconto: cart.vlproduto,
          retiradaImediata: cart.retiradaImediata,
          quantidade: cart.qtproduto,
          subTotal: cart.vlproduto * cart.qtproduto,
          obs: {
            ambiente: cart.ambiente,
            observacao: cart.obS_PROD,
          },
          remove: false,
          idProduto: cart.codigo,
          id: index,
          formGroupValue: [
            {
              formControlName: 'produto',
              value: `${cart.codigo} - ${cart.nomE_PRODUTO}`,
            },
            {
              formControlName: 'filial',
              value: cart.iD_FILIAL,
            },
            {
              formControlName: 'estoque',
              value: cart.qT_DISPONIVEL,
            },
            {
              formControlName: 'desconto',
              value: cart.desconto,
              unitaryValue: cart.vlprodutotabela,
              currentDiscount: cart.desconto,
              maxPercentDiscount: desconto$,
            },
            {
              formControlName: 'valorUnitario',
              value: cart.vlprodutotabela,
            },
            {
              formControlName: 'valorUnitarioComDesconto',
              value: cart.vlproduto,
            },
            {
              formControlName: 'retiradaImediata',
              value: cart.retiradaImediata,
              isEditable: false,
            },
            {
              formControlName: 'quantidade',
              value: cart.qtproduto,
              hasMultiple: cart.multiplo,
            },
            {
              formControlName: 'subTotal',
              value: cart.subTotal,
            },
            {
              formControlName: 'observation',
              value: {
                observacao: cart.obS_PROD,
                ambiente: cart.ambiente,
              },
              isEditable: true,
              ambientes: this.ambientes,
            },
            {
              formControlName: 'remove',
              value: false,
              content: {
                h1: 'Remover produto',
                p: 'Deseja realmente remover o produto?',
                button: 'Remover do carrinho',
              },
            },
          ],
        } as FormOrchestratorTableData<EditarOrcamentoDataDisplayColumns>;
      }
    );
    return MAP_DATA_COLUMNS;
  }

  private mappedTableFormData() {
    return map(() => {
      const getSelectProducts = this.allProducts$$.value;

      const replaceProducts = getSelectProducts.map((product, storageIndex) => {
        const stringIndex = storageIndex.toString();

        const formGroup =
          this.tableFormService.formGroups[this.keyTable].get(stringIndex);

        if (!formGroup || !formGroup.value) return product;

        const group = formGroup.value;

        const fnDesconto = this.fnDesconto(group);

        const fnSaveDesconto = this.fnSaveDesconto(group);

        product.desconto = fnSaveDesconto(group['desconto']);
        product.vlproduto = fnDesconto(group['desconto']);
        product.obS_PROD = group['observation']['observacao'];
        product.ambiente = group['observation']['ambiente'];
        product.qtproduto = group['quantidade'];
        product.subTotal = group['quantidade'] * group['valorUnitario'];
        product.remove = !!group['remove'];

        if (product.remove) this.removeControlsIndex.push(stringIndex);

        const subTotalPredicate = this.subTotalPredicate(
          storageIndex,
          group,
          fnDesconto
        );

        const updateDataByIndex =
          this.tableFormService.tableData$$.value.map(subTotalPredicate);

        this.tableFormService.tableData$$.next(updateDataByIndex);

        return product;
      });

      const lastAmbient = this.ambientes;

      const removeProducts = replaceProducts.filter(
        (cart: { remove: boolean }) => {
          if (
            replaceProducts.length === 1 &&
            replaceProducts[0].remove === true
          ) {
            this.toast.showError('Não é possível remover todos os produtos');
            const removeToFalse = replaceProducts.map((product) => {
              product.remove = false;
              return product;
            });
            this.cartLength = 2;
            return removeToFalse;
          }
          const isRemove = !cart.remove;
          return isRemove;
        }
      );

      this.ambientes = removeProducts.reduce((acc: string[], product) => {
        if (!product.ambiente) return [...acc];
        acc.push(product.ambiente);
        return acc;
      }, []);

      const ambienteEquals = lastAmbient
        ?.map((ambiente) => {
          return this.ambientes.includes(ambiente);
        })
        .includes(false);

      if (lastAmbient?.length !== this.ambientes?.length || ambienteEquals)
        this.cartLength++;

      const totalDesconto = removeProducts.reduce((acc: number, cart) => {
        return acc + (cart.vlprodutotabela - cart.vlproduto) * cart.qtproduto;
      }, 0);

      this.totalDiscount$$.next(+totalDesconto.toFixed(2));

      this.allProducts$$.next(removeProducts);
    });
  }

  subTotalPredicate(
    storageIndex: number,
    group: ValuesOf<{ [key: string]: FormControl<any> }>,
    fnDesconto: (desconto: { value: any; prefix: 'R$' | '%' }) => number
  ) {
    return (data: ItemsPedidoVendaFutura, controlIndex: number) => {
      if (storageIndex === controlIndex) {
        data.vlproduto = group['valorUnitario'] - fnDesconto(group['desconto']);
        data.vlproduto = group['valorUnitario'] - data.vlproduto;
        data.subTotal = group['quantidade'] * data.vlproduto;
      }
      return data;
    };
  }

  fnSaveDesconto(group: ValuesOf<{ [key: string]: FormControl<any> }>) {
    return (desconto: { value: any; prefix: 'R$' | '%' }) => {
      if (!desconto.value && desconto.value !== 0) return group['desconto'];
      const removeComma = desconto.value.toString().replace(',', '.');
      const valorUnitario = group['valorUnitario'];

      if (desconto.prefix !== '%') return +removeComma;

      const calcPercentage = (valorUnitario / 100) * +removeComma;

      const percentageDiscountToFixed = calcPercentage.toFixed(2);

      return +percentageDiscountToFixed;
    };
  }

  fnDesconto(group: ValuesOf<{ [key: string]: FormControl<any> }>) {
    return (desconto: { value: any; prefix: 'R$' | '%' }) => {
      if (!desconto.value && desconto.value !== 0)
        return group['valorUnitarioComDesconto'];
      const removeComma = desconto.value.toString().replace(',', '.');
      const valorUnitario = group['valorUnitario'];

      if (desconto.prefix !== '%') return valorUnitario - +removeComma;

      const calcPercentage = (valorUnitario / 100) * +removeComma;
      const valorUnitarioComDesconto = valorUnitario - calcPercentage;

      return valorUnitarioComDesconto;
    };
  }

  mappedDisplayData(changeDetectorRef: ChangeDetectorRef) {
    return this.allProducts$$.pipe(
      map((data) => {
        return this.createTableData(data);
      }),
      delay(100),
      tap((value) => {
        const totalCart = value.reduce((acc, curr) => {
          return acc + curr.valorUnitario * curr.quantidade;
        }, 0);

        this.orcamentoTotalCart$$.next(totalCart);

        this.hasData = value.length > 0;

        if (!this.table) return;

        changeDetectorRef.detectChanges();
        this.table.dataSource = value;
        this.table.changeDetectorRef.detectChanges();

        if (value.length !== this.cartLength) {
          this.cartLength = value.length;
          this.table.bsFormOrchestrator.ngOnInit();
        }
      })
    );
  }

  finishBuy(cartObservation: CartObservationsGroupComponent) {
    const selectedClient = this.selectClientService.getCliente();

    const produtos = this.allProducts$$.value.map(
      (product: ItemsPedidoVendaFutura) => {
        const calcDiscountPercentage =
          ((product.desconto ?? 0) * 100) / product.vlprodutotabela;
        return {
          qtproduto: product.qtproduto,
          iD_FILIAL: product.iD_FILIAL,
          pPERDESC: +calcDiscountPercentage.toFixed(2),
          pID_PRODUTO: product.codigo,
          qT_DISPONIVEL: product.qT_DISPONIVEL,
          pqtproduto: product.qtproduto,
          pvlproduto: product.vlproduto,
          pvlprodutotabela: product.vlprodutotabela,
          pobservacaO_ITEM: product.obS_PROD,
          pVLDESC: product.desconto,
          pvlprodutobase: product.vlprodutotabela,
          pAMBIENTE: product.ambiente,
        };
      }
    );

    const formaDeEntrega = selectedClient?.formaDeEntrega.toString();

    const pID_ENDER_ENTREGA =
      formaDeEntrega == '3' || formaDeEntrega == '4'
        ? 0
        : selectedClient?.endereco?.ID_ENDERECO ?? 0;

    const user = this.newApiUserService.currentUser();

    const requestData = {
      piD_CLIENTE: selectedClient?.id,
      piD_VENDEDOR: selectedClient?.vendedor,
      pfilial: selectedClient?.filial,
      pcobranca: selectedClient?.formaDePagamento,
      pmatricula: user?.idFuncionario,
      pSTATUSPENDENCIAORC: null,
      pparcela: selectedClient?.planoDePagamento,
      ptipovenda: selectedClient?.tipoVenda,
      pcodprofissional: selectedClient?.indication,
      pobservacaoent: cartObservation.entregaControl.value ?? null,
      obS_NOTA: cartObservation.notaControl.value ?? null,
      pSTATUS: 0,
      pOBSERVACAO: cartObservation.orcamentoControl.value ?? null,
      pDTENTREGA: null,
      pvlfrete: typeof this.freteControl.value == 'string' ? 0 : this.freteControl.value,
      pOBSERVACAO_ITEM: null,
      pTRANSPORTE: selectedClient?.formaDeEntrega,
      pseqpedidoentfut: null,
      pcpfcgc: selectedClient?.data.CPFCNPJ,
      pENDERECOENTREGA: selectedClient?.endereco?.LOGRADOURO_ENTREGA ?? '',
      pNUMEROENTREGA: selectedClient?.endereco?.NUMERO ?? '',
      pCOMPLEMENTOENTREGA: selectedClient?.endereco?.COMPLEMENTO_ENTREGA ?? '',
      pBAIRROENTREGA: selectedClient?.endereco?.BAIRRO_ENTREGA ?? '',
      pCIDADEENTREGA: selectedClient?.endereco?.CIDADE_ENTREGA ?? '',
      pUFENTREGA: selectedClient?.endereco?.UF_ENTREGA ?? '',
      pCEPENTREGA: selectedClient?.endereco?.CEP_ENTREGA ?? '',
      produtos: produtos,
      pID_ENDER_ENTREGA: pID_ENDER_ENTREGA,
      pSEQ_FORMAENTREGA: selectedClient?.formaDeEntrega.toString(),
      pAMBIENTE: '',
      valorDescontoRodape: 0,
      percentualDescontoRodape: 0,
    };

    this.orcamentoApiService
      .createOrcamento(requestData)
      .subscribe((orcamentoID: number) => {
        this.selectClientService.resetClient();
        this.selectClientService.getTotalCart();
        this.router.navigate([`./carrinho/compra-finalizada/${orcamentoID}`]);
      });
  }

  cancel() {
    this.selectClientService.resetClient();
  }
}
